import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';

// activeBusinessTypesOnly: hanya bidang usaha aktif, urut sesuai daftar (form pendaftaran).
// Tanpa opsi ini semua baris master_business_types dikembalikan (form perbaikan).
const useMasterData = ({ activeBusinessTypesOnly = false } = {}) => {
    const [provinces, setProvinces] = useState([]);
    const [businessTypes, setBusinessTypes] = useState([]);
    const [cities, setCities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch initial data (Provinces & Business Types) on mount
    useEffect(() => {
        let isMounted = true;

        const fetchInitialMasterData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Mengambil data secara paralel untuk optimasi performa
                const businessTypesQuery = activeBusinessTypesOnly
                    ? supabase.from('master_business_types').select('*').eq('is_active', true).order('sort_order', { ascending: true })
                    : supabase.from('master_business_types').select('*').order('name', { ascending: true });

                const [provincesResponse, businessTypesResponse] = await Promise.all([
                    supabase.from('master_provinces').select('*').order('name', { ascending: true }),
                    businessTypesQuery
                ]);

                if (provincesResponse.error) throw provincesResponse.error;
                if (businessTypesResponse.error) throw businessTypesResponse.error;

                if (isMounted) {
                    setProvinces(provincesResponse.data || []);
                    setBusinessTypes(businessTypesResponse.data || []);
                }
            } catch (err) {
                if (isMounted) {
                    setError(err.message || 'Terjadi kesalahan saat memuat master data.');
                    console.error('Error fetching initial master data:', err);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchInitialMasterData();

        return () => {
            isMounted = false;
        };
    }, [activeBusinessTypesOnly]);

    // Parameteric function to fetch cities based on province_id
    const fetchCities = useCallback(async (provinceId) => {
        if (!provinceId) {
            setCities([]);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const { data, error } = await supabase
                .from('master_cities')
                .select('*')
                .eq('province_id', provinceId)
                .order('name', { ascending: true });

            if (error) throw error;

            setCities(data || []);
        } catch (err) {
            setError(err.message || 'Terjadi kesalahan saat memuat data kabupaten/kota.');
            console.error('Error fetching cities:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    return {
        provinces,
        businessTypes,
        cities,
        isLoading,
        error,
        fetchCities
    };
};

export default useMasterData;